import {
    convertDecimalToPercentageForInputDisplay,
    convertPercentageForInputDisplayToDecimal,
    isEmpty,
} from '@fruitful/fruitils';
import {
    getActualGoalPriorityFromSynthetic,
    getSyntheticGoalPriorityFromActual,
    stripMoneyMapAccountFromRules,
    updateRuleAndGroupPrioritiesForGroupUpdate,
} from '@fruitful/money-map';
import {
    type DeepPartial,
    type MoneyMapRuleGroup,
    type MoneyMapRuleGroupUpsertDto,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { keyBy } from 'lodash';
import {
    type SubmitHandler,
    useForm,
    type UseFormReturn,
} from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    FootnoteRegular,
    Form,
    H6Emphasized,
    USE_FORM_MODE_DEFAULTS,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import FormDrawer from './FormDrawer';
import {
    GroupPercentageSplitFormModule,
    type GroupPercentageSplitFormSchema,
    groupPercentageSplitFormSchema,
} from './GroupPercentageSplitFormModule';
import {
    GroupSettingsFormModule,
    type GroupSettingsFormSchema,
    groupSettingsFormSchema,
} from './GroupSettingsFormModule';

interface GroupFormProps {
    group?: DeepPartial<MoneyMapRuleGroup>;
    priority: number;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onPressSave: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<void>;
    onPressRemove: (groupId?: string) => Promise<void>;
    onClose?: () => void;
    presentation?: 'drawer' | 'modal';
}

export const GroupForm = ({
    group,
    priority,
    moneyMap,
    onPressSave,
    onPressRemove,
    onClose,
    presentation,
}: GroupFormProps) => {
    const rules =
        group != null
            ? (moneyMap.moneyMapRules ?? []).filter(
                  (rule) => rule.moneyMapRuleGroupId === group.id,
              )
            : [];

    const groupFormSchema = z
        .object({
            ...groupSettingsFormSchema.shape,
            ...groupPercentageSplitFormSchema.shape,
        })
        .refine(({ percentageSplit }) => {
            const invalid = percentageSplit.find(({ percentage }) => {
                const percentageDecimal = !isEmpty(percentage)
                    ? convertPercentageForInputDisplayToDecimal(percentage)
                    : 0;
                return percentageDecimal <= 0 || percentageDecimal > 1;
            });
            return invalid == null;
        });

    type GroupFormSchema = z.infer<typeof groupFormSchema>;

    const formFunctions = useForm<GroupFormSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(groupFormSchema),
        defaultValues: {
            groupSettings: {
                name: group?.name ?? '',
                description: group?.description ?? '',
                // Get adjusted goal priority for display (goals are displayed as a 1-indexed list)
                priority: getSyntheticGoalPriorityFromActual(
                    priority,
                    moneyMap,
                ).toString(), // Input form handles numbers as strings,
            },
            percentageSplit:
                rules.length > 0
                    ? rules.map((rule) => ({
                          ruleId: rule.id,
                          percentage:
                              rule.percentage != null
                                  ? convertDecimalToPercentageForInputDisplay(
                                        rule.percentage,
                                    )
                                  : '',
                      }))
                    : [],
        },
    });

    const onSubmit: SubmitHandler<GroupFormSchema> = async ({
        groupSettings,
        percentageSplit,
    }) => {
        const updatedGroup: MoneyMapRuleGroupUpsertDto = {
            id: group?.id,
            moneyMapId: moneyMap?.id as string,
            priority: getActualGoalPriorityFromSynthetic(
                parseInt(groupSettings.priority),
                moneyMap,
            ),
            percentage: 1, // Hardcoded to always be 100%
            name: groupSettings.name,
            description: groupSettings.description,
        };

        // Exclude existing moneyMapAccount dto object from upsert call.
        const existingRules = stripMoneyMapAccountFromRules(
            moneyMap.moneyMapRules ?? [],
        );
        const existingRulesById = keyBy(existingRules, 'id');

        // Update percentages on existing rules
        percentageSplit.forEach(({ ruleId, percentage }) => {
            if (existingRulesById[ruleId] != null) {
                existingRulesById[ruleId].percentage =
                    convertPercentageForInputDisplayToDecimal(percentage);
            }
        });

        // Update priorities across all rules and groups
        const { rules: updatedRules, groups: updatedGroups } =
            updateRuleAndGroupPrioritiesForGroupUpdate(
                updatedGroup,
                Object.values(existingRulesById),
                moneyMap.moneyMapRuleGroups ?? [],
                'upsert',
            );

        await onPressSave({
            moneyMapRules: updatedRules,
            moneyMapRuleGroups: updatedGroups,
        });
    };

    const handleSubmit = formFunctions.handleSubmit;
    const isValid = formFunctions.formState.isValid;

    // Check if percentages add up to 100% for display
    const currentPercentageSplit = formFunctions.watch('percentageSplit') ?? [];
    const totalPercentage = currentPercentageSplit.reduce(
        (sum, { percentage }) => {
            const percentageDecimal = !isEmpty(percentage)
                ? convertPercentageForInputDisplayToDecimal(percentage)
                : 0;
            return sum + percentageDecimal;
        },
        0,
    );

    const hasInvalidPercentages =
        currentPercentageSplit.length > 0 &&
        Math.abs(totalPercentage - 1.0) > 0.01;

    return (
        <FormDrawer
            onClose={onClose}
            presentation={presentation}
            onSave={handleSubmit(onSubmit)}
            saveDisabled={!isValid || hasInvalidPercentages}
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <YStack gap={16}>
                    <XStack alignItems="center" justifyContent="space-between">
                        <View flexGrow={1}>
                            <H6Emphasized>
                                {group?.name ?? 'New Set'}
                            </H6Emphasized>
                        </View>
                        <Button
                            variant={ButtonVariant.TERTIARY}
                            size={ButtonSize.TINY}
                            width="auto"
                            tone="danger"
                            text="Remove set"
                            onPress={() => {
                                void onPressRemove(group?.id);
                            }}
                        />
                    </XStack>
                    <GroupSettingsFormModule
                        formFunctions={
                            formFunctions as never as UseFormReturn<GroupSettingsFormSchema>
                        }
                    />
                    {rules.length > 0 && (
                        <GroupPercentageSplitFormModule
                            rules={rules}
                            formFunctions={
                                formFunctions as never as UseFormReturn<GroupPercentageSplitFormSchema>
                            }
                        />
                    )}
                    {hasInvalidPercentages && (
                        <FootnoteRegular color="$red1000">
                            Error: Percentages must add up to 100%
                        </FootnoteRegular>
                    )}
                </YStack>
            </Form>
        </FormDrawer>
    );
};
